import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import {
  ActionCard,
  ActionCardGrid,
  ActionCardSection,
} from "@/components/ui/action-card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { useMyAppDbConfig } from "@/hooks/useMyAppDbConfig";
import { AppPropertiesSheet } from "@/components/portal/shared/AppPropertiesSheet";
import {
  Table2,
  Plus,
  LayoutGrid,
  Layers,
  RefreshCw,
  GitBranch,
  Database,
  Settings2,
  AlertTriangle,
} from "lucide-react";

export const Route = createFileRoute("/portal/myapp/")({
  component: MyAppHomePage,
});

const tableFeatures = [
  {
    title: "View Tables",
    description: "Browse and manage your existing tables.",
    icon: Table2,
    href: "/portal/myapp/tables",
    iconColor: "bg-blue-500",
  },
  {
    title: "Create Simple Table",
    description: "A standalone table for basic data capture.",
    icon: Plus,
    href: "/portal/myapp/tables?create=simple",
    iconColor: "bg-emerald-500",
  },
  {
    title: "Create Advanced Table",
    description: "Header/line patterns with linked tables.",
    icon: LayoutGrid,
    href: "/portal/myapp/tables?create=advanced",
    iconColor: "bg-purple-500",
  },
  {
    title: "Create Latest Only Table",
    description: "Import-replace table that keeps the latest data.",
    icon: RefreshCw,
    href: "/portal/myapp/tables?create=latest",
    iconColor: "bg-amber-500",
  },
  {
    title: "Create Versioned Table",
    description: "Import with version tracking and history.",
    icon: GitBranch,
    href: "/portal/myapp/tables?create=versioned",
    iconColor: "bg-indigo-500",
  },
  {
    title: "Create Virtual Table",
    description: "Live data from a SQL query — read-only, auto-refreshed.",
    icon: Database,
    href: "/portal/myapp/tables?create=virtual",
    iconColor: "bg-teal-500",
  },
];

const appFeatures = [
  {
    title: "View Apps",
    description: "Browse apps and their grouped tables.",
    icon: Layers,
    href: "/portal/myapp/apps",
    iconColor: "bg-cyan-500",
  },
];

function MyAppHomePage() {
  const { user } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, isAdmin, isPlatformAdmin } = usePermissions();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Creating a table is org admin and above only (MyApp enforces it too).
  // can("create","tables") alone is true for org_member: CP grants members
  // create on the whole MyApp gateway so they can create records.
  const canCreateTables = can("create", "tables") && (isAdmin || isPlatformAdmin);
  const canViewApps = can("view", "tables");
  const canSettings = can("manage", "tables");

  const organisationId = selectedOrganisation?.organisationId;
  const { isConfigured: dbConfigured, recheckDbConfig } =
    useMyAppDbConfig(organisationId);

  const displayName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ");
  const orgName = selectedOrganisation?.organisationName;

  return (
    <>
      <Helmet>
        <title>myApp | Portal</title>
      </Helmet>

      <div className="space-y-6">
        <PageHeader
          title="myApp"
          description={
            orgName
              ? `Welcome back, ${displayName}. You are working in ${orgName}.`
              : `Welcome back, ${displayName}.`
          }
          icon={LayoutGrid}
          eyebrow="Low-Code"
          gradient
        >
          {canSettings && (
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={() => setSettingsOpen(true)}
                className="rounded-full bg-white/80"
              >
                <Settings2 className="mr-2 h-4 w-4" />
                Settings
              </Button>
            </div>
          )}
        </PageHeader>

        {/* Settings Sheet */}
        <AppPropertiesSheet
          appName="myapp"
          testConnectionEndpoint="/api/v1/gateway/myapp/api/v1/test-connection"
          title="MyApp Settings"
          description="Configure the database connection and review installation instructions for the Excel Add-in."
          schemaHelp="The schema for your MyApp tables (e.g., myapp-demo)"
          organisationId={selectedOrganisation?.organisationId ?? ""}
          organisationName={orgName}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          onSaved={() => recheckDbConfig()}
          excelAddinTab
        />

        {/* DB config warning */}
        {dbConfigured === false && (
          <Alert variant="destructive" className="border-amber-500 bg-amber-50 text-amber-900">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <AlertDescription className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold">Database not configured</p>
                <p className="text-sm text-amber-700">
                  {canSettings
                    ? "Configure your database connection before creating or managing tables."
                    : "Ask your administrator to configure the database connection before you can use MyApp."}
                </p>
              </div>
              {canSettings && (
                <Button
                  size="sm"
                  onClick={() => setSettingsOpen(true)}
                  className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Settings2 className="mr-2 h-4 w-4" />
                  Configure Now
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}


        {/* Tables */}
        {can("view", "tables") && (
          <ActionCardSection title="Tables">
            <ActionCardGrid columns={3}>
              {tableFeatures
                .filter((f) => f.title === "View Tables" || canCreateTables)
                .map((feature) => (
                  <ActionCard
                    key={feature.href}
                    title={feature.title}
                    description={feature.description}
                    icon={feature.icon}
                    href={feature.href}
                    iconColor={feature.iconColor}
                  />
                ))}
            </ActionCardGrid>
          </ActionCardSection>
        )}

        {/* Apps */}
        {canViewApps && (
          <ActionCardSection title="Apps">
            <ActionCardGrid columns={3}>
              {appFeatures.map((feature) => (
                <ActionCard
                  key={feature.href}
                  title={feature.title}
                  description={feature.description}
                  icon={feature.icon}
                  href={feature.href}
                  iconColor={feature.iconColor}
                />
              ))}
            </ActionCardGrid>
          </ActionCardSection>
        )}
      </div>
    </>
  );
}
